import { AcBadge, FieldGrid } from '@/dashboard/ui';
import type { Lead360Input } from '@/dashboard/owner/lead-360-view';

/**
 * RETURNING-PATIENT-RECOGNITION-AND-LEAD-LINKAGE-V1 (Refs #315): the lead-time
 * returning-patient recognition panel on the Lead 360 work area.
 *
 * Renders `record.returningVisitor` as an ATTRIBUTED OBSERVATION -- a badge +
 * the fenced IDENTITY facts for a CONFIDENT single match, or a COUNT-ONLY line
 * for an AMBIGUOUS (shared-phone) match. Consumed straight off the raw record
 * (like goldenRecordLink); an OMITTED field renders NOTHING (a true new
 * prospect has zero overhead).
 *
 * BINDING LAWS honoured here:
 *  - FIVE-SECOND TEST: the badge + the next-action hint read in five seconds.
 *  - FACT-VERSUS-INFERENCE: a phone match is EVIDENCE, not identity proof -- the
 *    copy says the phone "matches an existing patient" and asks the operator to
 *    CONFIRM before linking; it never asserts the lead IS that patient. An
 *    AMBIGUOUS match names NO ONE (count only, no auto-pick).
 *  - CLINICAL WIRE LAW: only IDENTITY + RELATIONSHIP facts render (name,
 *    patient-since, last-visit DATE, home clinic). No clinical field exists on
 *    the wire to render.
 *  - The confident badge tone is 'brand' (attention/identity), NEVER 'positive'
 *    -- 'positive' is reserved for a CONFIRMED golden-record link, and a
 *    fingerprint match is not yet a confirmed link.
 */
export interface ReturningPatientPanelProps {
  readonly record: Lead360Input;
}

/** Month + year label (e.g. "Mar 2026") from an ISO timestamp; null-safe. */
function monthYearLabel(iso: string | null): string | null {
  if (iso === null || iso.trim() === '') return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    month: 'short',
    year: 'numeric',
  });
}

export default function ReturningPatientPanel({
  record,
}: ReturningPatientPanelProps): JSX.Element | null {
  const recognition = record.returningVisitor;
  if (recognition === undefined) {
    return null;
  }

  if (recognition.tier === 'ambiguous') {
    return (
      <section
        className="lead360-section returning-patient"
        aria-label="Returning patient"
        data-testid="returning-patient-panel"
        data-tier="ambiguous"
      >
        <div className="returning-patient__head">
          <AcBadge tone="info" testId="returning-patient-badge">
            Possible returning patient
          </AcBadge>
        </div>
        <p
          className="returning-patient__note"
          data-testid="returning-patient-ambiguous"
        >
          {recognition.candidateCount} patient records share this phone number.
          Open Patient Master to pick the right one before linking - do not
          assume which patient this is.
        </p>
      </section>
    );
  }

  return (
    <section
      className="lead360-section returning-patient"
      aria-label="Returning patient"
      data-testid="returning-patient-panel"
      data-tier="confident"
    >
      <div className="returning-patient__head">
        <AcBadge tone="brand" testId="returning-patient-badge">
          Returning patient
        </AcBadge>
        <span
          className="returning-patient__name"
          data-testid="returning-patient-name"
        >
          {recognition.recordName}
        </span>
      </div>
      <FieldGrid
        columns={2}
        label="Returning patient facts"
        testId="returning-patient-facts"
        fields={[
          {
            label: 'Patient since',
            value: monthYearLabel(recognition.knownSince),
            missingLabel: 'Not recorded',
            testId: 'returning-fact-since',
          },
          {
            label: 'Last visit',
            value: monthYearLabel(recognition.lastVisitDate),
            missingLabel: 'No visit on record',
            testId: 'returning-fact-lastvisit',
          },
          {
            label: 'Home clinic',
            value: recognition.homeClinicLabel,
            missingLabel: 'Not recorded',
            testId: 'returning-fact-clinic',
          },
        ]}
      />
      <p
        className="returning-patient__note"
        data-testid="returning-patient-hint"
      >
        This phone matches an existing patient record. Confirm it is the same
        person, then link this lead to that record.
      </p>
    </section>
  );
}
